import { useEffect, useRef, useState } from "react";

const DEFAULT_TYPING_SPEED = 1000;
const CHARACTERS_PER_TYPING_INTERVAL = 5;
const MAX_TIMEOUT = 200;

/**
 * Incrementally reveals text to create a typing effect.
 * If new text is a continuation of the previous text, it continues typing from where it left off.
 * If new text is completely different, it starts typing from the beginning.
 * @param text - The text to add the effect
 * @param speed - The number of milliseconds to type the remaining characters when text param changes.
 * @returns The text with the typing effect applied
 */
export const useTypingEffect = (
  text: string,
  speed: boolean | number = 1000,
): string => {
  const [displayedText, setDisplayedText] = useState("");
  const previousTextRef = useRef("");
  const currentIndexRef = useRef(0);
  const currentTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  // Keep a ref to the latest text so the visibility handler (registered once
  // with empty deps) can read the current value without a stale closure.
  const textRef = useRef(text);
  textRef.current = text;

  // When the tab becomes visible again, browsers un-throttle setInterval — but
  // if streaming finished while the tab was hidden the interval will crawl
  // through the remaining chars very slowly. Snap to full text on restore.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const currentText = textRef.current;
      if (currentIndexRef.current >= currentText.length) return;
      if (currentTimeoutIdRef.current) {
        clearInterval(currentTimeoutIdRef.current);
        currentTimeoutIdRef.current = null;
      }
      setDisplayedText(currentText);
      currentIndexRef.current = currentText.length;
      previousTextRef.current = currentText;
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // 1. If speed is 0, set the full text immediately
    if (speed === 0 || speed === false) {
      if (previousTextRef.current !== text) {
        setDisplayedText(text);
        previousTextRef.current = text;
      }
      return;
    }

    // 2. If the text is the same as before, do nothing
    if (text === previousTextRef.current) {
      return;
    }

    // 3. If there are no remaining characters, set the full text immediately
    const remainingChars = text.length - currentIndexRef.current;
    if (remainingChars <= 0) {
      setDisplayedText(text);
      currentIndexRef.current = text.length;
      previousTextRef.current = text;
      if (currentTimeoutIdRef.current) {
        clearInterval(currentTimeoutIdRef.current);
      }
      return;
    }

    // 4. If the new text is not a continuation of the previous text, reset state
    if (text.startsWith(previousTextRef.current) === false) {
      // Reset to start if the new text is not a continuation
      currentIndexRef.current = 0;
      setDisplayedText("");
      if (currentTimeoutIdRef.current) {
        clearInterval(currentTimeoutIdRef.current);
      }
    }
    previousTextRef.current = text;

    // 5. Perform typing effect
    if (currentTimeoutIdRef.current) {
      clearInterval(currentTimeoutIdRef.current);
    }
    const effectiveSpeed = speed === true ? DEFAULT_TYPING_SPEED : speed;
    const calculatedTimeout =
      remainingChars > 0
        ? (CHARACTERS_PER_TYPING_INTERVAL * effectiveSpeed) / remainingChars
        : 0;
    const effectiveTimeout = Math.min(calculatedTimeout, MAX_TIMEOUT);

    const typeNextCharacter = () => {
      if (currentIndexRef.current < text.length) {
        setDisplayedText(
          text.slice(
            0,
            currentIndexRef.current + CHARACTERS_PER_TYPING_INTERVAL,
          ),
        );
        currentIndexRef.current =
          currentIndexRef.current + CHARACTERS_PER_TYPING_INTERVAL;
      } else {
        if (currentTimeoutIdRef.current) {
          clearInterval(currentTimeoutIdRef.current);
        }
      }
    };

    currentTimeoutIdRef.current = setInterval(
      typeNextCharacter,
      effectiveTimeout,
    );
  }, [text, speed]);

  return displayedText;
};
